import React from "react";
import { useShow, useCustomMutation } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Typography, Tag, Table, Card, Button, Spin, Space } from "antd";
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    FileTextOutlined,
    ShoppingCartOutlined,
    SyncOutlined,
    PrinterOutlined,
} from "@ant-design/icons";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;
const { Column } = Table;

export const PurchaseOrderShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "purchaseorders",
        meta: {
            fields: [
                "id",
                "orderNumber",
                "vendorId",
                "currencyTypeCode",
                "exchangeRate",
                "subTotal",
                "totalTaxableAmount",
                "totalTax",
                "totalAmount",
                "status",
                "orderDate",
                "deliveryDate",
                "documentUrl",
                "createdAt",
                "updatedAt",
                {
                    items: [
                        "id",
                        "itemCode",
                        "itemName",
                        "itemClassCode",
                        "quantity",
                        "quantityUnitCode",
                        "unitPrice",
                        "lineAmount",
                        "taxRate",
                        "taxAmount",
                        "totalAmount",
                        {
                            inventoryItem: [
                                "id",
                                "itemCode",
                                "itemName",
                                "itemClassificationCode"
                            ]
                        }
                    ]
                },
                {
                    vendor: [
                        "id",
                        "name",
                        "tradeName",
                        "tpin",
                        "bankName",
                        "bankAccount",
                        {
                            vendorContacts: [
                                "id",
                                "phoneNumber",
                                "email",
                                "isPrimary"
                            ]
                        },
                        {
                            vendorAddresses: [
                                "id",
                                "houseNo",
                                "street",
                                "city",
                                "province",
                                "country",
                                "isPrimary"
                            ]
                        }
                    ]
                }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    const { mutate: confirm } = useCustomMutation();
    const { mutate: cancel } = useCustomMutation();
    const { mutate: convert } = useCustomMutation();
    const { mutate: generatePdf } = useCustomMutation();
    const { mutate: viewPdf } = useCustomMutation();

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Confirmed":
                return "green";
            case "Draft":
                return "blue";
            case "Converted":
                return "purple";
            case "Cancelled":
                return "red";
            default:
                return "default";
        }
    };

    const handleConfirm = () => {
        confirm({
            url: `purchaseorders/${record?.id}/confirm`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Purchase order confirmed successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleCancel = () => {
        cancel({
            url: `purchaseorders/${record?.id}/cancel`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Purchase order cancelled successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleConvert = () => {
        convert({
            url: `purchaseorders/${record?.id}/convert`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Purchase order converted to purchase successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleGeneratePdf = () => {
        generatePdf({
            url: `purchaseorders/${record?.id}/generate-pdf`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Purchase order PDF generated successfully",
                type: "success",
            }),
        }, {
            onSuccess: (data: any) => {
                const documentUrl = data?.data?.DocumentUrl || data?.DocumentUrl;
                if (documentUrl) {
                    window.open(documentUrl, "_blank");
                }
                queryResult.refetch();
            }
        });
    };

    const handleViewPdf = () => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, '_blank');
        } else {
            viewPdf({
                url: `purchaseorders/${record?.id}/pdf`,
                method: "get",
                successNotification: false,
            }, {
                onSuccess: (data: any) => {
                    const documentUrl = data?.data?.DocumentUrl || data?.DocumentUrl;
                    if (documentUrl) {
                        window.open(documentUrl, '_blank');
                    }
                },
                onError: () => ({
                    message: "Error",
                    description: "PDF not available. Please Print first.",
                    type: "error",
                }),
            });
        }
    };

    const formatAddress = (vendor: any): string => {
        const address = vendor?.vendorAddresses?.find((a: any) => a.isPrimary) ||
            vendor?.vendorAddresses?.[0];
        return address ?
            `${address.houseNo} ${address.street}, ${address.city}, ${address.province}, ${address.country}` :
            '-';
    };

    // Build action buttons based on status
    const getActionButtons = () => {
        const actions: any[] = [];

        // Print button
        actions.push({
            label: "Print",
            icon: <PrinterOutlined />,
            onClick: record?.documentUrl ? handleViewPdf : handleGeneratePdf,
            variant: "outlined",
        });

        // Confirm button (only for Draft)
        if (record?.status === "Draft") {
            actions.push({
                label: "Confirm",
                icon: <CheckCircleOutlined />,
                onClick: handleConfirm,
                variant: "solid",
                type: "primary",
            });
        }

        // Convert button (only for Confirmed)
        if (record?.status === "Confirmed") {
            actions.push({
                label: "Convert to Purchase",
                icon: <SyncOutlined />,
                onClick: handleConvert,
                variant: "solid",
                type: "primary",
            });
        }

        return actions;
    };

    // More menu items
    const moreMenuItems = [
        ...(record?.status === "Draft" || record?.status === "Confirmed" ? [{
            key: "cancel",
            label: "Cancel Order",
            icon: <CloseCircleOutlined />,
            onClick: handleCancel,
            danger: true,
        }] : []),
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="show-page-wrapper">
            <PageHeader
                title={`Purchase Order #${record?.orderNumber || ''}`}
                titleIcon={<FileTextOutlined />}
                breadcrumbs={[
                    { title: "Purchase Orders", href: "/purchaseorders" },
                    { title: "Show Purchase Order" },
                ]}
                actions={getActionButtons()}
                moreMenuItems={moreMenuItems.length > 0 ? moreMenuItems : undefined}
            >
                {/* Purchase Order-specific mini stat cards */}
                <div className="mini-stats-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Total Amount</div>
                        <div className="mini-stat-value success">${record?.totalAmount?.toFixed(2) || '0.00'}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <Tag color={getStatusColor(record?.status)} style={{ margin: 0 }}>
                            {record?.status}
                        </Tag>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Currency</div>
                        <div className="mini-stat-value">{record?.currencyTypeCode || 'USD'}</div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Purchase Order Details Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Purchase Order Details</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Order Number:</span>
                                <span className="info-value">{record?.orderNumber || '-'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag color={getStatusColor(record?.status)}>{record?.status}</Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Order Date:</span>
                                <span className="info-value">
                                    <DateField value={record?.orderDate} format="YYYY-MM-DD" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Delivery Date:</span>
                                <span className="info-value">
                                    <DateField value={record?.deliveryDate} format="YYYY-MM-DD" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vendor Details Card */}
                {record?.vendor && (
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Vendor Details</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Name:</span>
                                    <span className="info-value">
                                        <Link to={`/vendors/show/${record.vendorId}`}>
                                            <Text strong style={{ color: 'var(--color-primary)' }}>{record.vendor.name}</Text>
                                        </Link>
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Trade Name:</span>
                                    <span className="info-value">{record.vendor.tradeName || '-'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">TPIN:</span>
                                    <span className="info-value">{record.vendor.tpin || '-'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Phone:</span>
                                    <span className="info-value">
                                        {record.vendor.vendorContacts?.find((c: any) => c.isPrimary)?.phoneNumber ||
                                            record.vendor.vendorContacts?.[0]?.phoneNumber || '-'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Email:</span>
                                    <span className="info-value">
                                        {record.vendor.vendorContacts?.find((c: any) => c.isPrimary)?.email ||
                                            record.vendor.vendorContacts?.[0]?.email || '-'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Bank:</span>
                                    <span className="info-value">{record.vendor.bankName || '-'}</span>
                                </div>
                                <div className="info-item full-width">
                                    <span className="info-label">Address:</span>
                                    <span className="info-value">{formatAddress(record.vendor)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Financial Summary Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Financial Summary</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Currency:</span>
                                <span className="info-value">{record?.currencyTypeCode || '-'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Exchange Rate:</span>
                                <span className="info-value">{record?.exchangeRate?.toFixed(4) || '-'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Subtotal:</span>
                                <span className="info-value">${record?.subTotal?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Total Tax:</span>
                                <span className="info-value">${record?.totalTax?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Total Amount:</span>
                                <span className="info-value" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--color-success)' }}>
                                    ${record?.totalAmount?.toFixed(2) || '0.00'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Purchase Order Items Card */}
                <Card title="Purchase Order Items" className="data-table-card">
                    <Table
                        dataSource={record?.items}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 800 }}
                    >
                        <Column
                            title="Item Code"
                            width={120}
                            render={(_, item: any) => item?.inventoryItem?.itemCode || item?.itemCode || '-'}
                        />
                        <Column
                            title="Item Name"
                            width={200}
                            render={(_, item: any) => item?.inventoryItem?.itemName || item?.itemName || '-'}
                        />
                        <Column
                            title="Classification"
                            width={120}
                            render={(_, item: any) => item?.inventoryItem?.itemClassificationCode || item?.itemClassCode || '-'}
                        />
                        <Column
                            dataIndex="quantity"
                            title="Quantity"
                            width={100}
                            render={(value: number) => value?.toFixed(2)}
                            align="right"
                        />
                        <Column
                            dataIndex="taxRate"
                            title="Tax %"
                            width={80}
                            render={(value: number) => `${value?.toFixed(1)}%`}
                            align="center"
                        />
                        <Column
                            dataIndex="taxAmount"
                            title="Tax Amount"
                            width={120}
                            render={(value: number) => `$${value?.toFixed(2)}`}
                            align="right"
                        />
                        <Column
                            dataIndex="totalAmount"
                            title="Total"
                            width={120}
                            render={(value: number) => (
                                <Text strong>${value?.toFixed(2)}</Text>
                            )}
                            align="right"
                        />
                    </Table>
                </Card>

                {/* System Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">System Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Updated At:</span>
                                <span className="info-value">
                                    <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
